import React, { useState } from "react";
import { useIntl } from "react-intl";

import { BookingFormTipsSection } from "@components/Result/BookingFormTipsSection";
import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";

interface BookingFormTipsProps {
  backgroundImageUrl?: boolean;
}

function BookingFormTips({
  backgroundImageUrl,
}: BookingFormTipsProps): React.ReactElement {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const sections = [
    {
      id: "BookingFormTips.SameNames",
      title: <FormattedMessage id="BookingFormTips.SameNames" />,
      content: (
        <>
          <p>
            <FormattedMessage id="BookingFormTips.SameNames.line1" />
          </p>
          <p>
            {(() => {
              const text = intl.formatMessage({
                id: "BookingFormTips.SameNames.childYoungTraveler",
              });
              const [boldPart, rest] = text.split(": ");
              return (
                <>
                  <strong>{boldPart}: </strong>
                  {rest}
                </>
              );
            })()}
          </p>
          <p>
            {(() => {
              const text = intl.formatMessage({
                id: "BookingFormTips.SameNames.twoAdults",
              });
              const [boldPart, rest] = text.split(": ");
              return (
                <>
                  <strong>{boldPart}: </strong>
                  {rest}
                </>
              );
            })()}
          </p>
          <p>
            {(() => {
              const text = intl.formatMessage({
                id: "BookingFormTips.SameNames.lowCostTickets",
              });
              const [boldPart, rest] = text.split(": ");
              return (
                <>
                  <strong>{boldPart}: </strong>
                  {rest}
                </>
              );
            })()}
          </p>
          <div className="break" />
          <p style={{ fontSize: "14px" }}>
            <strong>
              <FormattedMessage id="BookingFormTips.SameNames.emiratesChinaAirlines" />
            </strong>
          </p>
          <p>
            {intl.formatMessage({
              id: "BookingFormTips.SameNames.emiratesChinaAirlines.line1",
            })}
          </p>
          <p>
            {intl.formatMessage({
              id: "BookingFormTips.SameNames.emiratesChinaAirlines.line2",
            })}
          </p>
          <p>
            {(() => {
              const text = intl.formatMessage({
                id:
                  "BookingFormTips.SameNames.emiratesChinaAirlines.chinaAirlines",
              });
              const [boldPart, rest] = text.split(": ");
              return (
                <>
                  <strong>{boldPart}: </strong>
                  {rest}
                </>
              );
            })()}
          </p>
          <p>
            {(() => {
              const text = intl.formatMessage({
                id: "BookingFormTips.SameNames.emiratesChinaAirlines.emirates",
              });
              const [boldPart, rest] = text.split(": ");
              return (
                <>
                  <strong>{boldPart}: </strong>
                  {rest}
                </>
              );
            })()}
          </p>
        </>
      ),
    },
    {
      id: "BookingFormTips.NameTooLong",
      title: <FormattedMessage id="BookingFormTips.NameTooLong" />,
      content: (
        <>
          <p>
            <FormattedMessage id="BookingFormTips.NameTooLong.line1" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.namesOnTravelDocument" />{" "}
            </strong>
            <i>
              <FormattedMessage id="BookingFormTips.NameTooLong.line2" />
            </i>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.firstName" />{" "}
              <FormattedMessage id="BookingFormTips.otherNames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.NameTooLong.line3" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.lastName" />{" "}
              <FormattedMessage id="BookingFormTips.Surnames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.NameTooLong.line4" />
          </p>
        </>
      ),
    },
    {
      id: "BookingFormTips.NameSuffix",
      title: <FormattedMessage id="BookingFormTips.NameSuffix" />,
      content: (
        <>
          <p>
            <FormattedMessage id="BookingFormTips.NameSuffix.line1" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.namesOnTravelDocument" />{" "}
            </strong>
            <i>
              <FormattedMessage id="BookingFormTips.NameSuffix.line2" />
            </i>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.firstName" />{" "}
              <FormattedMessage id="BookingFormTips.otherNames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.NameSuffix.line3" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.lastName" />{" "}
              <FormattedMessage id="BookingFormTips.Surnames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.NameSuffix.line1" />
          </p>
        </>
      ),
    },
    {
      id: "BookingFormTips.SpecialCharacters",
      title: <FormattedMessage id="BookingFormTips.SpecialCharacters" />,
      content: (
        <>
          <p>
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line1" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="General.example" />
              {" 1"}
            </strong>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.namesOnTravelDocument" />{" "}
            </strong>
            <i>
              <FormattedMessage id="BookingFormTips.SpecialCharacters.line2" />
            </i>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.firstName" />{" "}
              <FormattedMessage id="BookingFormTips.otherNames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line3" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.lastName" />{" "}
              <FormattedMessage id="BookingFormTips.Surnames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line4" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="General.example" />
              {" 2"}
            </strong>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.namesOnTravelDocument" />{" "}
            </strong>
            <i>
              <FormattedMessage id="BookingFormTips.SpecialCharacters.line5" />
            </i>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.firstName" />{" "}
              <FormattedMessage id="BookingFormTips.otherNames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line6" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.lastName" />{" "}
              <FormattedMessage id="BookingFormTips.Surnames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line7" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="General.example" />
              {" 3"}
            </strong>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.namesOnTravelDocument" />{" "}
            </strong>
            <i>
              <FormattedMessage id="BookingFormTips.SpecialCharacters.line8" />
            </i>
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.firstName" />{" "}
              <FormattedMessage id="BookingFormTips.otherNames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line9" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.lastName" />{" "}
              <FormattedMessage id="BookingFormTips.Surnames" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.SpecialCharacters.line10" />
          </p>
        </>
      ),
    },
    {
      id: "BookingFormTips.OtherScenarios",
      title: <FormattedMessage id="BookingFormTips.OtherScenarios" />,
      content: (
        <>
          <p>
            <FormattedMessage id="BookingFormTips.OtherScenarios.line1" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.OtherScenarios.noFirstName" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.OtherScenarios.noFirstNameExample" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.OtherScenarios.noLastName" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.OtherScenarios.noLastNameExample" />
          </p>
          <p>
            <strong>
              <FormattedMessage id="BookingFormTips.OtherScenarios.oneLetterName" />
            </strong>{" "}
            <FormattedMessage id="BookingFormTips.OtherScenarios.oneLetterNameExample" />
          </p>
        </>
      ),
    },
  ];

  return (
    <>
      <div
        className={
          backgroundImageUrl
            ? "views-modal-warning-background"
            : "views-modal-warning"
        }
      >
        <strong>
          <FormattedMessage id="BookingFormTips.important" />
        </strong>{" "}
        <FormattedMessage id="BookingFormTips.notice" />
        <button
          type="button"
          className="link pointer"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            marginLeft: "10px",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <FormattedMessage id="BookingFormTips.viewTips" />
        </button>
      </div>
      <Modal
        isActive={isOpen}
        onClose={() => setIsOpen(false)}
        modalTitle={intl.formatMessage({ id: "BookingFormTips.title" })}
      >
        <div className="view-modal">
          <div className="views-modal-description">
            <FormattedMessage id="BookingFormTips.description" />
          </div>
          <div
            className="view-modal-links-container"
            style={{
              display: "flex",
              flexDirection: "column",
              paddingTop: "10px",
            }}
          >
            {sections.map((section) => (
              <BookingFormTipsSection
                key={section.id}
                buttonText={section.title}
                content={section.content}
              />
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}

export default BookingFormTips;
